<template>
    <div>
        <message-builder
            ref="messageBuilderRef"
            v-model="message.body"
            v-model:mediaUrl="message.media_url"
            v-model:mediaContentType="message.media_content_type"
            @selected:snippet="isSnippetUsed = true"
            @submit="submit()"
            @schedule="
                message.send_at = $event;
                submit();
            "
            @blur="handleMessageBuilderBlur"
            :disableSchedule="currentOrgUnit"
            :disabled="
                isNotActive() ||
                existingTransactionalStatus === 'opt_out' ||
                hasPhoneNumber === false ||
                contactNotFound
            "
            :hideNumbers="!isLg"
            maxHeight
        >
            <template v-slot:before="slotProps">
                <slot name="before" v-bind="slotProps"></slot>
            </template>
            <template v-slot:actions>
                <vx-dropdown-item v-if="FEATURES.message_hub_ai" @click.prevent="aiModal = true">
                    <span class="flex items-center gap-1">
                        <font-awesome-icon widthAuto :icon="faWandMagicSparkles" class="text-sm"></font-awesome-icon>
                        <span>AI Message</span>
                        <experimental-pill class="pointer-events-none" placement="right"></experimental-pill>
                    </span>
                </vx-dropdown-item>
            </template>
            <template v-if="isNotActive()" v-slot:alert>
                <vx-alert color="info" :closeable="false"> <strong>Disabled:</strong> Team is not active. </vx-alert>
            </template>
            <template v-else-if="existingTransactionalStatus === 'opt_out'" v-slot:alert>
                <vx-alert color="info" :closeable="false">
                    <strong>Disabled:</strong> Contact has opted out of transactional messages.
                </vx-alert>
            </template>
            <template v-else-if="hasPhoneNumber === false" v-slot:alert>
                <vx-alert color="info" :closeable="false">
                    <strong>Disabled:</strong> No phone numbers are available to send messages.
                </vx-alert>
            </template>
            <template v-else-if="contactNotFound" v-slot:alert>
                <vx-alert color="info" :closeable="false">
                    <strong>Disabled:</strong>
                    {{
                        onCorporate()
                            ? 'This contact is no longer available.'
                            : 'This contact is not associated with the current group.'
                    }}
                </vx-alert>
            </template>
        </message-builder>
        <message-builder-message-hub-generation
            v-model:visible="aiModal"
            v-if="FEATURES.message_hub_ai"
            :contact-id="contact.id"
            @selected="message.body = $event"
        />
    </div>
</template>
<script setup>
import MessageBuilder from '~/components/general/message-builder/MessageBuilder.vue';
import { inject, onMounted, watch, ref } from 'vue';
import { VxDropdownItem, VxAlert, VxSwal } from '@voxie/frontend-components';
import { faWandMagicSparkles } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ExperimentalPill from '~/components/general/ExperimentalPill.vue';
import MessageBuilderMessageHubGeneration from '~/components/general/message-builder/modals/MessageBuilderMessageHubGeneration.vue';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { isNotActive } from '~/utils/team';
import { useConversation } from '~/composables/useConversation';
import { actionsClient } from '~/services';
import { catchRequestError } from '~/utils/request';
import { useMessageHub } from '~/components/message-hub/useMessageHub';
import { useResponsiveLayout } from '~/composables/useResponsiveLayout';
import { useMessageHubDrafts } from '~/components/message-hub/useMessageHubDrafts';

const FEATURES = inject('FEATURES');
const { isLg } = useResponsiveLayout();
const { contact, contactNotFound, existingTransactionalStatus } = useContactDetails();
const { onCorporate, currentOrgUnit } = useCurrentGroup();
const { hasPhoneNumber, checkPhoneNumber } = useMessageHub();
const { message, deliverMessage, activeThread } = useConversation();
const { saveDraft, clearDraft, getDraft } = useMessageHubDrafts();

const messageBuilderRef = ref();
const aiModal = ref(false);
const isSnippetUsed = ref(false);

onMounted(() => {
    checkPhoneNumber();
});

watch(
    () => activeThread.value?.contact_id,
    async () => {
        await messageBuilderRef.value?.focusTextarea();

        if (activeThread.value) {
            const threadContext = { teamId: activeThread.value.team_id, groupId: activeThread.value.group_id };
            const draft = getDraft(threadContext, activeThread.value.contact_id);

            if (draft) {
                message.body = draft.body || '';
                message.media_url = draft.media_url || null;
                message.media_content_type = draft.media_content_type || null;
            }
        }
    },
    {
        immediate: true,
    }
);

const submit = async () => {
    try {
        const draftThread = { ...activeThread.value };
        await deliverMessage(message);

        // Clear draft after successful send
        clearDraft({ teamId: draftThread.team_id, groupId: draftThread.group_id }, draftThread.contact_id);

        if (isSnippetUsed.value) {
            await actionsClient.send('snippet_used');
            isSnippetUsed.value = false;
        }
    } catch (e) {
        VxSwal.fire({
            title: 'Whoops!',
            text: catchRequestError(e),
            icon: 'error',
        });
    }
};

const handleMessageBuilderBlur = () => {
    if (!activeThread.value) {
        return;
    }

    const threadContext = { teamId: activeThread.value.team_id, groupId: activeThread.value.group_id };

    if (!message.body.trim()) {
        clearDraft(threadContext, activeThread.value.contact_id);
        return;
    }

    saveDraft(threadContext, activeThread.value.contact_id, {
        body: message.body,
        media_url: message.media_url,
        media_content_type: message.media_content_type,
    });
};
</script>
